import { PageHeader } from '@/components/PageHeader'
import { ScheduleCalendar } from '@/components/schedule/ScheduleCalendar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { invalidateBusinessInfoQuery } from '@/hooks/queries/useBusinessInfo'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { ConnectToWebsocket } from '@/lib/socket.io/components/ConnectToWebsocket'
import { QuestionIcon } from '@phosphor-icons/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, CalendarIcon } from 'lucide-react'
import { ScheduleArticleDialog } from '../../../components/ScheduleArticleDialog'

export const Route = createFileRoute('/_dashboard/schedule/')({
  component: SchedulePage,
  beforeLoad: () => {
    // ensure schedule is loaded
    queryClient.ensureQueryData(orpc.articles.getListOfArticles.queryOptions())
  },
})

function AutomaticPublishingCard() {
  const { activeBusiness } = useActiveBusiness()
  const updateBusinessMutation = useMutation(
    orpc.business.updateBusinessDetails.mutationOptions({
      onSettled: () => {
        invalidateBusinessInfoQuery()
      },
    }),
  )

  function handleAutomaticPublishingChange(checked: boolean) {
    if (!activeBusiness) return
    updateBusinessMutation.mutate({
      id: activeBusiness.id,
      automaticPublishing: checked,
    })
  }

  return (
    <div className="flex p-4 px-6 flex-col sm:flex-row gap-4 items-center mb-4 bg-transparent rounded-xl border border-border">
      <div className="flex-1 w-full">
        <div className="flex gap-2 font-medium">
          Automatic Publishing
          <Tooltip>
            <TooltipTrigger>
              <QuestionIcon className="w-5 h-5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm text-wrap">
              When enabled, articles will automatically be published after
              they've been written. Disable this if you want to review and
              manually publish each article before it's published.
            </TooltipContent>
          </Tooltip>
        </div>
        {/* <CardDescription>
          When enabled, the AI will automatically publish articles after they've
          been written. Otherwise, you will need to manually publish.
        </CardDescription> */}
      </div>
      <div>
        <div className="flex gap-2">
          <Label htmlFor="automatic-publishing">Enabled</Label>
          <Switch
            defaultChecked={activeBusiness?.automaticPublishing}
            id="automatic-publishing"
            onCheckedChange={handleAutomaticPublishingChange}
          />
        </div>
      </div>
    </div>
  )
}

function SchedulePage() {
  return (
    <>
      <ConnectToWebsocket />
      <div className="py-8 px-8">
        <div className="mx-auto max-w-[1600px]">
          {/* Header */}
          <PageHeader
            title="AI Content Schedule"
            description="These are the articles your AI has scheduled to be created."
            className="mb-8"
          />

          {/* Stats */}
          {/* <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-4">
            <div className="p-3 bg-white rounded-lg border border-border">
              <div className="text-2xl font-bold text-gray-900">
                {articleQuery.data?.length}
              </div>
              <div className="text-sm text-gray-600">Total Scheduled</div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-border">
              <div className="text-2xl font-bold text-blue-600">1</div>
              <div className="text-sm text-gray-600">Scheduled</div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-border">
              <div className="text-2xl font-bold text-yellow-600">1</div>
              <div className="text-sm text-gray-600">In Progress</div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-border">
              <div className="text-2xl font-bold text-green-600">1</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
          </div> */}

          <AutomaticPublishingCard />

          <IntegrationSetupAlert />

          {/* Controls */}
          <div className="flex flex-col gap-4 justify-end mb-6 md:flex-row">
            {/* Search */}
            {/* <div>
              <Input
                icon={<SearchIcon />}
                type="text"
                placeholder="Search scheduled articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-70"
              />
            </div> */}

            <div className="flex w-full md:w-auto gap-3">
              <ScheduleArticleDialog>
                <Button className="gap-2 md:flex-auto flex-1">
                  <CalendarIcon className="w-4 h-4" />
                  Schedule Content
                </Button>
              </ScheduleArticleDialog>
            </div>
          </div>

          {/* Articles Grid */}
          <ScheduleCalendar />
        </div>
      </div>
    </>
  )
}

function IntegrationSetupAlert() {
  const integrations = useQuery(
    orpc.integrations.getIntegrations.queryOptions(),
  )
  const enabledIntegrations = integrations.data?.filter(
    (integration) => integration.enabled,
  )

  if (!enabledIntegrations || enabledIntegrations.length > 0) return null

  return (
    <Alert className="mb-4">
      <AlertCircle />
      <AlertTitle>Heads up!</AlertTitle>
      <AlertDescription>
        You don't have an integration setup for publishing. In order for Rocket
        Rank to automatically publish articles, you need at least one
        integration.
        <Button variant="link" className="p-0 m-0 h-auto mt-2" asChild>
          <Link to="/settings/integrations">Setup an integration</Link>
        </Button>
      </AlertDescription>
    </Alert>
  )
}
